import React, { useEffect, useState, useMemo } from "react";
import Loading from "../../../../common-components/Loading";
import { getLtmKpiAPI } from "../../../../api/reports-api";
import {
  formatActualsThroughFromMonths,
  formatActualsThroughDate,
  headerActualsDate,
  buildLtmKpiApiFilters,
  formatLtmKpiPeriodLabel,
  formatLtmKpiPeriodFromWindow,
  monthNameToKey,
} from "./plTimeFilterUtils";
import {
  usePlByProgramStyles,
  PlPageHeader,
  PlChartPanel,
  PlFilterGroup,
  PL_FONT,
  PL_INK,
  PL_MUTED,
  PL_SUBTLE,
  PL_SURFACE_3,
  PL_BORDER_LT,
} from "./plByProgramTheme";
import { useReportFonts } from "../../reportFonts";
import {
  AD_NAVY,
  AD_MIDNIGHT,
  CHART_DONUT_COLORS,
  CHART_END_CUSTOMER_COLORS,
} from "../../reportColorPalette";

/* ─────────────────────────────────────────────────────────────── */
/* Per-chart colors — Agile Defense brand palette                 */
/* ─────────────────────────────────────────────────────────────── */
const AWARD_COLORS = CHART_DONUT_COLORS;
const COST_RANK_COLORS = CHART_DONUT_COLORS;
const CUSTOMER_COLORS = CHART_DONUT_COLORS;
const END_CUSTOMER_COLORS = CHART_END_CUSTOMER_COLORS;

const LTM_MODE_OPTS = [
  { value: "ltm", label: "Last 12 Months" },
  { value: "single", label: "Selected Month" },
];

/* Dashed "NATSEC" grouping arc — drawn around every End-Customer slice
   except this one. */
const NS_EXCLUDED_GROUP = "FEDCIV";
const NS_ARC_COLOR = AD_NAVY;
const NS_ARC_LABEL = "NATSEC";
// A small dashed-line glyph ("- -", two thin bars) for NATSEC's legend entry, instead of the
// default solid swatch every other (real, mutually-exclusive) slice gets — NATSEC is a dashed
// overlay ring spanning several slices, not a slice of its own, and the old solid-rect icon made
// it look like one. Kept close enough to the legend icon's own ~25x14 box that it isn't scaled
// down to a near-invisible hairline to fit, while still reading as a thin line, not a block.
const NS_ARC_LEGEND_ICON =
  "path://M0,0 L5,0 L5,3 L0,3 Z M11,0 L16,0 L16,3 L11,3 Z";

// TBD is a placeholder/unknown-value bucket, not a real category — always render it in this
// fixed neutral gray instead of whatever color its rank/position would otherwise land on, so it
// reads as "unclassified" rather than being mistaken for a meaningful category.
const TBD_COLOR = "#9CA3AF";

/** Power BI cosmetic labels — API keys stay canonical for tooltips via `rawName`. */
const COMPETITION_DISPLAY_LABELS = {
  "FULL AND OPEN": "F&O",
  "F&O": "F&O",
  "SMALL BUSINESS": "SB",
  SB: "SB",
  "SOLE SOURCE": "SBSA",
  UNRESTRICTED: "UNRESTRICTED",
  SBA: "SBA",
  "8(A)": "8(A)",
  "(Blank)": "(Blank)",
};

const COST_TYPE_DISPLAY_LABELS = {
  FFP: "FIXED PRICE",
  "FIXED PRICE": "FIXED PRICE",
  "Cost Plus": "CPFF",
  CPFF: "CPFF",
  "T&M": "T&M",
  UNBILL: "UNBILL",
  HYBRID: "HYBRID",
  TBD: "TBD",
  "(Blank)": "(Blank)",
};

const BU_DISPLAY_LABELS = {
  CS: "CITSVCS",
};

function displayLabel(name, labelMap) {
  return labelMap[name] || name;
}

/**
 * Coerce a raw API value to a number, tolerating pre-formatted currency
 * strings (e.g. "$25,000", "-$1,200", or accounting-style "(25,000)" for
 * negatives) in addition to plain numbers — a bare `Number(raw)` would
 * silently turn any of those string forms into NaN and lose the value
 * entirely instead of excluding it.
 */
function parseNumericValue(raw) {
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : 0;
  if (raw == null) return 0;
  const s = String(raw).trim();
  if (!s) return 0;
  const isParenNegative = /^\(.*\)$/.test(s);
  const cleaned = s.replace(/[()$,\s]/g, "");
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return 0;
  return isParenNegative ? -Math.abs(n) : n;
}

/**
 * Sum values that share the same display label (e.g. FFP + FIXED PRICE → one slice).
 * Negative values are pulled out into `excluded` — they never enter the pie chart
 * or its percentage math, and are surfaced separately in a "Not Included in
 * Chart" table instead of silently skewing the positive-value percentages.
 *
 * Positive and negative raw contributions are merged into SEPARATE buckets per
 * display label (not merged together first, then sign-checked) — otherwise a
 * negative raw entry sharing a label with a positive one (e.g. "FFP": 500000
 * and "FIXED PRICE": -30000 both merging to "FIXED PRICE" under
 * COST_TYPE_DISPLAY_LABELS) would net out to a positive total and the
 * negative would silently vanish from both the chart and the exclusion table
 * instead of being reported.
 */
function objToChartData(obj, labelMap) {
  const positive = {};
  const negative = {};
  for (const [rawName, raw] of Object.entries(obj || {})) {
    const name = labelMap ? displayLabel(rawName, labelMap) : rawName;
    const value = parseNumericValue(raw);
    if (value < 0) {
      negative[name] = (negative[name] || 0) + value;
    } else {
      positive[name] = (positive[name] || 0) + value;
    }
  }
  const chartData = Object.entries(positive)
    .map(([name, value]) => ({ name, rawName: name, value }))
    .sort((a, b) => b.value - a.value);
  const excluded = Object.entries(negative)
    .map(([name, value]) => ({ name, rawName: name, value }))
    .sort((a, b) => a.value - b.value);
  return { chartData, excluded };
}

function fmtContractRevenue(v) {
  return (Number(v) || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Accounting format for the "Not Included in Chart" table, e.g. -25000 -> "($25,000)". */
function fmtAccounting(v) {
  const n = Number(v) || 0;
  const abs = Math.abs(n).toLocaleString("en-US", { maximumFractionDigits: 0 });
  return n < 0 ? `($${abs})` : `$${abs}`;
}

/* ─────────────────────────────────────────────────────────────── */
/* Rank-based color map: sort by value desc (stable), assign colors by position */
function buildRankColorMap(rawData, rankColors) {
  const indexed = rawData.map((d, i) => ({
    name: d.name,
    value: Number(d.value) || 0,
    i,
  }));
  indexed.sort((a, b) => (b.value !== a.value ? b.value - a.value : a.i - b.i));
  const map = {};
  indexed.forEach(({ name }, rank) => {
    map[name] = rankColors[rank] ?? rankColors[rankColors.length - 1];
  });
  return map;
}

/** % of a chart's total held by every slice EXCEPT `exceptName` — the same
 *  grouping the NATSEC dashed overlay arc draws, exposed here so it can also
 *  be shown as a standalone stat box. Null when there's nothing to compute. */
function computeGroupPct(chartData, exceptName) {
  if (!exceptName || !chartData?.length) return null;
  const total = chartData.reduce((sum, d) => sum + (Number(d.value) || 0), 0);
  if (!total) return null;
  const exceptValue = Number(
    chartData.find((d) => d.name === exceptName)?.value || 0,
  );
  return ((total - exceptValue) / total) * 100;
}

/* ECharts donut option builder                                    */
/* colors    — per-chart palette array                            */
/* centerX   — horizontal centre (shift left to allow legend)     */
/* ─────────────────────────────────────────────────────────────── */
function buildDonutOption(
  rawData,
  colors,
  centerX = "38%",
  colorByLabel = null,
  dimensionLabel = "",
  hiddenNames = null,
  nsExcept = null,
  excluded = [],
) {
  // Single source of truth for the donut's vertical center — the pie series
  // and the NATSEC overlay ring both read from this instead of each
  // repeating their own "54%" literal.
  const centerY = "54%";
  const total = rawData.reduce((sum, d) => sum + (Number(d.value) || 0), 0);
  // National-Security arc: when nsExcept is set, render that slice LAST so the
  // dashed overlay ring's gap lines up with it. Legend keeps the value order.
  const hasNsArc = nsExcept != null && rawData.some((d) => d.name === nsExcept);
  const nsExceptValue = hasNsArc
    ? Number(rawData.find((d) => d.name === nsExcept)?.value || 0)
    : 0;
  const orderedRaw = hasNsArc
    ? [
        ...rawData.filter((d) => d.name !== nsExcept),
        ...rawData.filter((d) => d.name === nsExcept),
      ]
    : rawData;
  const data = orderedRaw.map((d, i) => {
    const value = Number(d.value) || 0;
    const originalPct = total ? (value / total) * 100 : 0;
    const hidden = hiddenNames?.has(d.name);
    const color =
      (colorByLabel && colorByLabel[d.name]) || colors[i % colors.length];
    const pctLabel = originalPct.toFixed(1);
    /* Show callout text for every visible slice (incl. thin ones); hide line if no text */
    const showCallout = !hidden && Number(pctLabel) !== 0;
    return {
      name: d.name,
      value,
      originalPct,
      silent: hidden,
      itemStyle: {
        color,
        opacity: hidden ? 0 : 1,
      },
      label: { show: showCallout },
      labelLine: { show: showCallout },
    };
  });
  // Ghost slices (value 0, non-interactive) for each excluded name.
  // ECharts will not paint a legend entry that has no matching series data
  // point — it stays in the legend *model* but never reaches the canvas —
  // so a real (if zero-value) data point is required for it to render at all.
  // A 0-value slice already has zero angular span (nothing draws on the
  // donut regardless of opacity) — opacity must stay 1 here, since the
  // legend swatch inherits its fill from this itemStyle, and opacity: 0
  // made the legend's red circle render hollow/invisible too.
  const dataWithExcluded = [
    ...data,
    ...excluded.map((d) => ({
      name: d.name,
      value: 0,
      originalPct: 0,
      silent: true,
      itemStyle: { color: "#DC2626", opacity: 1 },
      label: { show: false },
      labelLine: { show: false },
    })),
  ];
  return {
    backgroundColor: "transparent",
    tooltip: {
      trigger: "item",
      backgroundColor: PL_SURFACE_3,
      borderColor: PL_BORDER_LT,
      borderWidth: 1,
      padding: [10, 14],
      textStyle: {
        fontSize: 13,
        color: PL_INK,
        fontFamily: PL_FONT,
        fontWeight: 500,
      },
      extraCssText:
        "border-radius:10px;box-shadow:0 8px 24px rgba(0,0,0,0.12);",
      formatter: (p) => {
        const pct = p.data?.originalPct ?? 0;
        const revenue = fmtContractRevenue(p.value);
        return (
          `<div style="display:grid;grid-template-columns:auto auto;column-gap:20px;row-gap:2px;` +
          `font-family:Roboto, Helvetica, Arial, sans-serif;color:${PL_INK};font-size:12px;font-weight:500;">` +
          `<span style="text-align:right;">${dimensionLabel}</span>` +
          `<span style="text-align:left;">${p.name}</span>` +
          `<span style="text-align:right;">Revenue</span>` +
          `<span style="text-align:left;">${revenue} (${pct.toFixed(2)}%)</span>` +
          `</div>`
        );
      },
    },
    legend: {
      selectedMode: true,
      selected: Object.fromEntries(
        [...rawData, ...excluded].map((d) => [d.name, true]),
      ),
      data: [
        ...rawData.map((d) => ({ name: d.name })),
        ...(hasNsArc
          ? [
              {
                name: NS_ARC_LABEL,
                icon: NS_ARC_LEGEND_ICON,
                itemStyle: { color: NS_ARC_COLOR },
              },
            ]
          : []),
        // Excluded (negative-total) buckets are listed too, so the legend
        // shows every category even though these have no chart slice —
        // styled in red (matching the "Not Included in Chart" table) to
        // mark them as excluded rather than a normal toggle-off.
        ...excluded.map((d) => ({
          name: d.name,
          icon: "circle",
          itemStyle: { color: "#DC2626" },
        })),
      ],
      orient: "vertical",
      right: 8,
      top: "middle",
      type: "scroll",
      icon: "circle",
      itemWidth: 8,
      itemHeight: 8,
      itemGap: 8,
      pageTextStyle: { fontSize: 11, color: PL_MUTED, fontFamily: PL_FONT },
      // `rich` must live under `textStyle.rich` — ECharts silently ignores a
      // `{styleName|text}` token from `formatter` if `rich` sits directly on
      // `legend` instead, rendering the raw "{styleName|text}" as literal text.
      textStyle: {
        fontSize: 11,
        color: PL_MUTED,
        fontFamily: PL_FONT,
        rich: {
          off: {
            color: "#CBD5E1",
            textDecoration: "line-through",
            fontFamily: PL_FONT,
            fontSize: 11,
          },
        },
      },
      formatter: (name) => {
        const short = name.length > 22 ? name.slice(0, 20) + "…" : name;
        // Excluded entries use the same text styling as every other legend
        // item (only the circle icon is red) — no special-casing needed here.
        if (hiddenNames?.has(name)) return `{off|${short}}`;
        return short;
      },
    },
    series: [
      {
        type: "pie",
        selectedMode: false,
        radius: ["32%", "70%"],
        center: [centerX, centerY],
        startAngle: 90,
        minShowLabelAngle: 0,
        avoidLabelOverlap: true,
        label: {
          show: true,
          formatter: (p) => {
            const pct = p.data?.originalPct ?? 0;
            const pctLabel = pct.toFixed(1);
            return `{nm|${p.name}}\n{pc|${pctLabel}%}`;
          },
          rich: {
            nm: {
              fontSize: 10,
              color: PL_SUBTLE,
              fontFamily: PL_FONT,
              lineHeight: 15,
            },
            pc: {
              fontSize: 10,
              color: PL_MUTED,
              fontFamily: PL_FONT,
              lineHeight: 15,
            },
          },
        },
        labelLine: {
          show: true,
          length: 10,
          length2: 14,
          lineStyle: { color: "#CBD5E1", width: 1 },
        },
        emphasis: {
          itemStyle: { shadowBlur: 10, shadowColor: "rgba(0,0,0,0.15)" },
          scale: true,
          scaleSize: 4,
        },
        data: dataWithExcluded,
      },
      // Overlay ring: a dashed NATSEC arc spanning every slice except
      // nsExcept (gap at that slice). Same center/startAngle as the main
      // ring so the gap aligns; non-interactive, no labels/tooltip.
      ...(hasNsArc
        ? [
            {
              type: "pie",
              silent: true,
              legendHoverLink: false,
              // Near-hairline band: inner ≈ outer radius so the sector's two
              // arc edges merge into a single clean dashed line (no "railroad"
              // double line); nudged just outside the 70% ring for a small gap.
              radius: ["73.4%", "73.7%"],
              center: [centerX, centerY],
              startAngle: 90,
              label: { show: false },
              labelLine: { show: false },
              emphasis: { disabled: true },
              tooltip: { show: false },
              data: [
                {
                  name: NS_ARC_LABEL,
                  value: total - nsExceptValue,
                  itemStyle: {
                    color: "transparent",
                    borderColor: NS_ARC_COLOR,
                    borderWidth: 1.1,
                    borderType: [5, 8],
                  },
                },
                {
                  name: nsExcept,
                  value: nsExceptValue,
                  itemStyle: { color: "transparent", borderWidth: 0 },
                },
              ],
            },
          ]
        : []),
    ],
  };
}

function LtmDonutPanel({
  title,
  chartData,
  excluded,
  colors,
  centerX,
  colorByLabel,
  colorByRank,
  dimensionLabel,
  nsExcept,
  classes,
}) {
  const [hiddenNames, setHiddenNames] = useState(() => new Set());

  const dataKey = useMemo(
    () => chartData.map((d) => `${d.name}:${d.value}`).join("|"),
    [chartData],
  );

  useEffect(() => {
    setHiddenNames(new Set());
  }, [dataKey]);

  const effectiveColorByLabel = useMemo(() => {
    const base = colorByRank ? buildRankColorMap(chartData, colors) : colorByLabel;
    // Force TBD to its own fixed color regardless of rank/explicit map — see TBD_COLOR above.
    return chartData.some((d) => d.name === "TBD")
      ? { ...base, TBD: TBD_COLOR }
      : base;
  }, [colorByRank, chartData, colors, colorByLabel]);

  const option = useMemo(
    () =>
      buildDonutOption(
        chartData,
        colors,
        centerX,
        effectiveColorByLabel,
        dimensionLabel,
        hiddenNames,
        nsExcept,
        excluded,
      ),
    [
      chartData,
      colors,
      centerX,
      effectiveColorByLabel,
      dimensionLabel,
      hiddenNames,
      nsExcept,
      excluded,
    ],
  );

  const onEvents = useMemo(
    () => ({
      legendselectchanged: (e) => {
        if (!e?.name) return;
        setHiddenNames((prev) => {
          const next = new Set(prev);
          if (e.selected) next.delete(e.name);
          else next.add(e.name);
          return next;
        });
      },
    }),
    [],
  );

  const groupPct = useMemo(
    () => computeGroupPct(chartData, nsExcept),
    [chartData, nsExcept],
  );
  const total = useMemo(
    () => chartData.reduce((sum, d) => sum + (Number(d.value) || 0), 0),
    [chartData],
  );

  return (
    <PlChartPanel
      title={title}
      option={option}
      classes={classes}
      onEvents={onEvents}
      sideContent={
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <TotalStatBox total={total} />
          {nsExcept && <GroupPctBox label={NS_ARC_LABEL} pct={groupPct} />}
          <LtmExcludedTable excluded={excluded} />
        </div>
      }
    />
  );
}

/** Compact stat box showing the chart's total — on the left side of the card,
    next to the chart, same treatment as the negative-value callouts below it
    (styled box, label on top, bold value), instead of inside the donut. */
function TotalStatBox({ total }) {
  return (
    <div
      style={{
        width: 158,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 4,
        padding: "10px 12px",
        background: PL_SURFACE_3,
        border: `1px solid ${PL_BORDER_LT}`,
        borderRadius: 10,
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          color: PL_MUTED,
          fontFamily: PL_FONT,
        }}
      >
        Total
      </div>
      <div
        style={{
          fontSize: 16,
          fontWeight: 800,
          color: PL_INK,
          fontFamily: PL_FONT,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {fmtAccounting(total)}
      </div>
    </div>
  );
}

/** Compact stat box showing one named group's share of the chart's total,
    e.g. the NATSEC grouping on the End Customer donut. */
function GroupPctBox({ label, pct }) {
  if (pct == null) return null;
  return (
    <div
      style={{
        width: 158,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 4,
        padding: "10px 12px",
        background: PL_SURFACE_3,
        border: `1px solid ${PL_BORDER_LT}`,
        borderRadius: 10,
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          color: PL_MUTED,
          fontFamily: PL_FONT,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 20,
          fontWeight: 800,
          color: AD_NAVY,
          fontFamily: PL_FONT,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {pct.toFixed(1)}%
      </div>
    </div>
  );
}

/** Compact side table listing values excluded from the pie chart/percentages
    (currently: negatives), shown in accounting format, e.g. ($25,000). */
function LtmExcludedTable({ excluded }) {
  if (!excluded?.length) return null;
  return (
    <div
      style={{
        width: 158,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        background: PL_SURFACE_3,
        border: `1px solid ${PL_BORDER_LT}`,
        borderRadius: 10,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "7px 10px",
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          color: PL_MUTED,
          borderBottom: `1px solid ${PL_BORDER_LT}`,
          fontFamily: PL_FONT,
          flexShrink: 0,
        }}
      >
        Not Included in Chart
      </div>
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        {excluded.map((d, i) => (
          <div
            key={d.name}
            title={d.name}
            style={{
              padding: "6px 10px",
              borderBottom:
                i < excluded.length - 1 ? `1px solid ${PL_BORDER_LT}` : "none",
              fontFamily: PL_FONT,
            }}
          >
            <div
              style={{
                fontSize: 11,
                color: PL_INK,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {d.name}
            </div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "#DC2626",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {fmtAccounting(d.value)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── */
/* Main component                                                  */
/* ─────────────────────────────────────────────────────────────── */
export default function LtmKpi({ filters, actualsThroughDate }) {
  const classes = usePlByProgramStyles();
  useReportFonts();

  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(false);
  /** params.ltmMode — only honored by API when a month is selected */
  const [ltmMode, setLtmMode] = useState("ltm");

  const hasMonthSelected = useMemo(
    () =>
      (filters?.monthName || []).map(monthNameToKey).filter(Boolean).length > 0,
    [filters?.monthName],
  );

  const apiFilters = useMemo(
    () => buildLtmKpiApiFilters(filters),
    [filters],
  );
  const apiParams = useMemo(
    () => (hasMonthSelected ? { ltmMode } : {}),
    [hasMonthSelected, ltmMode],
  );
  const apiRequestKey = useMemo(
    () => JSON.stringify({ filters: apiFilters, params: apiParams }),
    [apiFilters, apiParams],
  );

  useEffect(() => {
    setLoading(true);
    setApiData(null);
    getLtmKpiAPI(apiFilters, apiParams)
      .then((res) => {
        if (res?.data) setApiData(res.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [apiRequestKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const { chartData: awardData, excluded: awardExcluded } = objToChartData(
    apiData?.byAwardType,
  );
  const { chartData: costData, excluded: costExcluded } = objToChartData(
    apiData?.byCostType,
    COST_TYPE_DISPLAY_LABELS,
  );
  const { chartData: buData, excluded: buExcluded } = objToChartData(
    apiData?.byBu,
    BU_DISPLAY_LABELS,
  );
  const { chartData: customerData, excluded: customerExcluded } =
    objToChartData(apiData?.byEndCustomer);
  const { chartData: competitionData, excluded: competitionExcluded } =
    objToChartData(apiData?.byCompetitionType, COMPETITION_DISPLAY_LABELS);

  const metaTop = apiData
    ? `${apiData.version?.name || "Live EAC"} · FY ${apiData.fiscalYear}`
    : "Live EAC · FY 2026";
  const headerDate = headerActualsDate(
    filters?.year,
    loading,
    formatActualsThroughDate(apiData?.actualThroughMonth),
    formatActualsThroughFromMonths(
      apiData?.actualMonths,
      apiData?.windowStart,
      apiData?.windowEnd,
    ),
  );
  const ltmPeriodLabel = useMemo(() => {
    const fromApi = formatLtmKpiPeriodFromWindow(
      apiData?.windowStart,
      apiData?.windowEnd,
    );
    return fromApi || formatLtmKpiPeriodLabel(filters, ltmMode);
  }, [apiData, filters, ltmMode]);

  return (
    <>
      {loading && <Loading />}
      <div className={classes.root}>
        <div className={classes.container}>
          <PlPageHeader
            classes={classes}
            eyebrow="P&L by Program"
            title="LTM KPI Charts"
            accent="Portfolio Mix"
            meta="Contract Revenue Breakdown · 5 Dimensions"
            metaTop={metaTop}
            date={headerDate}
          />

          <div className={classes.sectionCard}>
            <div className={classes.sectionToolbar}>
              <div className={classes.sectionTitleWrap}>
                <div className={classes.sectionAccent} />
                <div>
                  <div className={classes.sectionTitle}>
                    Revenue Breakdown
                  </div>
                  <div className={classes.sectionSubtitle}>
                    LTM Portfolio Distribution by Category
                  </div>
                </div>
              </div>
              <div
                style={{
                  marginLeft: "auto",
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  flexShrink: 0,
                }}
              >
                {hasMonthSelected && (
                  <PlFilterGroup
                    label="Window"
                    options={LTM_MODE_OPTS}
                    value={ltmMode}
                    onChange={setLtmMode}
                    classes={classes}
                  />
                )}
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 14,
                    color: AD_MIDNIGHT,
                    fontFamily: PL_FONT,
                    textAlign: "right",
                    whiteSpace: "nowrap",
                  }}
                >
                  {ltmPeriodLabel}
                </div>
              </div>
            </div>
            {!loading && (
              <div
                className={classes.chartsRow}
                style={{ gridTemplateColumns: "repeat(2, 1fr)" }}
              >
                <LtmDonutPanel
                  title="Revenue by Award Type"
                  chartData={awardData}
                  excluded={awardExcluded}
                  colors={AWARD_COLORS}
                  centerX="38%"
                  colorByRank
                  dimensionLabel="Award Type"
                  classes={classes}
                />
                <LtmDonutPanel
                  title="Revenue by BU"
                  chartData={buData}
                  excluded={buExcluded}
                  colors={CUSTOMER_COLORS}
                  centerX="32%"
                  colorByRank
                  dimensionLabel="BU"
                  classes={classes}
                />
                <LtmDonutPanel
                  title="Revenue by Cost Type"
                  chartData={costData}
                  excluded={costExcluded}
                  colors={COST_RANK_COLORS}
                  centerX="38%"
                  colorByRank
                  dimensionLabel="Cost Type"
                  classes={classes}
                />
                <LtmDonutPanel
                  title="Revenue by End Customer"
                  chartData={customerData}
                  excluded={customerExcluded}
                  colors={CUSTOMER_COLORS}
                  colorByLabel={END_CUSTOMER_COLORS}
                  centerX="32%"
                  dimensionLabel="End Customer"
                  nsExcept={NS_EXCLUDED_GROUP}
                  classes={classes}
                />
                <LtmDonutPanel
                  title="Revenue by Competition Type"
                  chartData={competitionData}
                  excluded={competitionExcluded}
                  colors={COST_RANK_COLORS}
                  centerX="38%"
                  colorByRank
                  dimensionLabel="Competition Type"
                  classes={classes}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
